import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * AB#2652/AB#2654 — the host seam for metadata-sourced field options.
 *
 * A field declares `metaOptions: { field, kind, multi }`: its options are the STATUS VALUES
 * (kind "statuses") or FIELD NAMES (kind "fields") of the entity named by the driving `field`'s
 * value on the same record — a follow-on rule offering the source document's real lifecycle
 * statuses, a document type offering the document's real field names. The host resolves an entity
 * type to that metadata; the shared control renders it. A host that provides nothing (the designer
 * preview) leaves the field as the plain text input, so editing is never blocked.
 *
 * Deliberately a CONTEXT rather than another renderer prop, for the reason lookupCreate.tsx gives:
 * the loader props are drilled through ~28 call sites and every one is a chance to miss a branch —
 * a seam that silently fails to arrive in a nested grid would leave free text in exactly the places
 * this primitive exists to fix.
 */
export interface EntityMetaOptions {
    /** The entity's status members, in the authored enum spelling ("CODE" or "CODE:Label"). */
    // AB#2913 — these come straight from the entity's status field values, so they carry the same
    // bare-code-or-{ value, label } union.
    statusValues: (string | { value: string; label?: string | null })[]
    /** The entity's top-level fields — name plus display label. */
    fields: { name: string; label: string }[]
}

export type FetchEntityMeta = (entityType: string) => Promise<EntityMetaOptions>

const EntityMetaContext = createContext<FetchEntityMeta | undefined>(undefined)

export function EntityMetaProvider(
    { handler, children }: { handler: FetchEntityMeta; children: ReactNode },
) {
    return <EntityMetaContext.Provider value={handler}>{children}</EntityMetaContext.Provider>
}

/** The host's entity-metadata loader, or undefined when the host provides none. */
export function useEntityMeta(): FetchEntityMeta | undefined {
    return useContext(EntityMetaContext)
}

// Cached per handler identity (≈ per host render tree) keyed by entityType, so a grid of follow-on
// rows all naming one source document fetches its definition once. A FAILED fetch is evicted so a
// later mount retries rather than caching the outage — the loadLookupOptions idiom.
const entityMetaCache = new WeakMap<FetchEntityMeta, Map<string, Promise<EntityMetaOptions>>>()

function loadEntityMeta(handler: FetchEntityMeta, entityType: string): Promise<EntityMetaOptions> {
    let cache = entityMetaCache.get(handler)
    if (!cache) { cache = new Map(); entityMetaCache.set(handler, cache) }
    const byKey = cache
    let promise = byKey.get(entityType)
    if (!promise) {
        promise = handler(entityType)
        promise.catch(() => byKey.delete(entityType))
        byKey.set(entityType, promise)
    }
    return promise
}

/**
 * Subscribe to an entity's (cached) metadata options. `options` stays undefined while loading;
 * `failed` flips true when the fetch rejects — the caller then falls back to the free-text input,
 * because a metadata outage must never block editing the record.
 */
export function useEntityMetaOptions(
    entityType: string | undefined,
    handler: FetchEntityMeta | undefined,
): { options: EntityMetaOptions | undefined; failed: boolean } {
    const [state, setState] = useState<{ options: EntityMetaOptions | undefined; failed: boolean }>({ options: undefined, failed: false })
    useEffect(() => {
        setState({ options: undefined, failed: false })
        if (!entityType || !handler) { return }
        let ignore = false
        loadEntityMeta(handler, entityType)
            .then(opts => { if (!ignore) { setState({ options: opts, failed: false }) } })
            .catch(() => { if (!ignore) { setState({ options: undefined, failed: true }) } })
        return () => { ignore = true }
    }, [entityType, handler])
    return state
}
